import { Link } from "react-router-dom";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { nn } from "date-fns/locale";
import { CalendarDays, Check, Inbox, Mail, Ticket, Users, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeading } from "@/components/admin/PageHeader";
import { StatusPill } from "@/components/admin/StatusPill";
import { fmtRange } from "@/lib/format";
import { cn } from "@/lib/utils";

const rel = (iso) => { try { return formatDistanceToNowStrict(parseISO(iso), { addSuffix: true, locale: nn }); } catch { return ""; } };
const ICON = { booking: Inbox, booking_decision: Check, message: Mail, ticket: Ticket, entry: CalendarDays };
const TO = {
  booking: (a) => ({ to: "/admin/bookingar", state: { open: a.id } }), booking_decision: (a) => ({ to: "/admin/bookingar", state: { open: a.id } }),
  message: () => ({ to: "/admin/meldingar" }), ticket: () => ({ to: "/admin/billettar" }), entry: (a) => ({ to: `/admin/kalender?view=list&omfang=alle&rediger=${a.id}` }),
};

/** Last things that happened – førespurnader, meldingar, billettar og nye oppføringar. */
export const LatestActivity = ({ activity = [], loading }) => (
  <section data-testid="dashboard-activity">
    <SectionHeading title="Siste aktivitet" description="Førespurnader, meldingar, billettar og nye oppføringar" />
    {loading ? <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-9 rounded-xl" />)}</div> : !activity.length ? (
      <p className="py-2 text-[13px] text-muted-foreground">Ingen aktivitet enno.</p>
    ) : (
      <ol className="divide-y divide-hairline" data-testid="dashboard-activity-list">
        {activity.map((a, i) => {
          const Icon = ICON[a.type] || Users;
          const link = (TO[a.type] || TO.entry)(a);
          const rejected = a.type === "booking_decision" && a.status === "avvist";
          return (
            <li key={`${a.type}-${a.id}-${i}`}>
              <Link to={link.to} state={link.state} className="-mx-2 flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-secondary/50" data-testid={`activity-${a.type}-${a.id}`}>
                <span className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-full", rejected ? "bg-[hsl(var(--status-cancelled-bg))] text-status-cancelled" : a.type === "booking_decision" ? "bg-[hsl(var(--status-confirmed-bg))] text-status-confirmed" : "bg-secondary text-muted-foreground")}>
                  {rejected ? <X className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />}
                </span>
                <span className="min-w-0 flex-1"><span className="block truncate text-[13px]">{a.title}</span>{a.sub && <span className="block truncate text-[11.5px] text-muted-foreground">{a.type === "entry" && /^\d{4}-\d{2}-\d{2}$/.test(a.sub) ? fmtRange(a.sub, a.sub) : a.sub}</span>}</span>
                {a.status && a.status !== "lest" && <StatusPill status={a.status} className="hidden sm:inline-flex" />}
                <span className="shrink-0 text-[11.5px] text-muted-foreground tabular" title={a.at}>{rel(a.at)}</span>
              </Link>
            </li>
          );
        })}
      </ol>
    )}
  </section>
);
